import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { assetUrl } from "@/lib/api";

const STEPS = [
  {
    num: "01",
    title: "Parlez-nous de votre projet",
    sub: "Tout commence par vos envies.",
    text: "Rénovation, extension, aménagement ou construction : décrivez-nous votre projet, votre logement ou terrain, vos besoins et votre budget envisagé.",
    text2: "Ces premières informations nous permettent de mieux comprendre votre projet avant le premier échange.",
    img: assetUrl("/api/static/models/epure-int1.webp"),
  },
  {
    num: "02",
    title: "Donnons une forme claire à votre projet",
    sub: "Avant de commencer les travaux, il faut savoir où l'on va.",
    text: "Nous échangeons avec vous pour préciser les besoins, les priorités et les contraintes du projet.",
    text2: "Selon sa nature, cette préparation peut intégrer l'estimation, les plans, la conception, les choix techniques ou les démarches nécessaires à sa réalisation.",
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    num: "03",
    title: "Entourez-vous des bons professionnels",
    sub: "Un bon projet repose aussi sur les bonnes personnes.",
    text: "CozyHome facilite la recherche et la coordination des professionnels adaptés aux différents besoins du projet.",
    text2: "Vous gardez ainsi une lecture plus simple des intervenants, des prestations proposées et des prochaines décisions à prendre.",
    img: assetUrl("/api/static/redesign/conseil-pros.webp"),
  },
  {
    num: "04",
    title: "Suivez l'avancement sans perdre le fil",
    sub: "Votre niveau d'accompagnement s'adapte à votre besoin.",
    text: null,
    supportExpertise: true,
    img: assetUrl("/api/static/chantiers/demo/toiture.webp"),
  },
  {
    num: "05",
    title: "Arrivez sereinement à la fin du projet",
    sub: "Les derniers détails comptent autant que les premiers.",
    text: "À l'approche de la fin des travaux, nous vous aidons, selon l'accompagnement choisi, à préparer cette dernière étape et à faire le point sur le projet réalisé.",
    text2: null,
    img: assetUrl("/api/static/redesign/hero.webp"),
  },
];

export default function ProcessPage() {
  const navigate = useNavigate();
  return (
    <PageShell>
      <PageHero
        testid="page-process-hero"
        label="Votre projet, étape par étape"
        title={<>Vous avez le projet. CozyHome vous aide à construire <span className="italic text-sage">le chemin pour le réaliser.</span></>}
        sub="Un projet de travaux peut vite devenir complexe. Notre rôle est de rendre chaque étape plus lisible et de vous permettre d'avancer avec les bons interlocuteurs, au bon moment."
      />

      <section className="bg-ivory pb-20 sm:pb-28">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="flex flex-col gap-16 sm:gap-24">
            {STEPS.map((s, i) => (
              <Reveal key={s.num}>
                <div
                  data-testid={`process-timeline-step-${i}`}
                  className={`grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-16 ${
                    i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className="overflow-hidden rounded-3xl">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading={i === 0 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : "auto"}
                      decoding="async"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                    />
                  </div>
                  <div>
                    <p className="font-display text-6xl font-medium text-wood sm:text-7xl">{s.num}</p>
                    <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                      {s.title}
                    </h2>
                    <p className="mt-2 text-sm font-semibold italic text-cognac sm:text-base">{s.sub}</p>

                    {s.supportExpertise ? (
                      <div className="mt-5 space-y-4">
                        <div className="rounded-2xl border border-[#EDEAE3] bg-white p-5">
                          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sage">CozyHome Essentiel</p>
                          <p className="mt-2 text-sm leading-relaxed text-stone-500">
                            Vous souhaitez rester impliqué dans votre projet ? Avec CozyHome
                            Essentiel, vous gardez la maîtrise de vos travaux tout en
                            bénéficiant de notre accompagnement pour préparer, organiser et
                            sécuriser votre projet.
                          </p>
                        </div>
                        <div className="rounded-2xl bg-navy p-5">
                          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cognac-light">Pack complet</p>
                          <p className="mt-2 text-sm leading-relaxed text-white/70">
                            Vous souhaitez nous confier le suivi de votre chantier ? Avec le
                            Pack complet, CozyHome vous accompagne de la préparation
                            jusqu'à la réception, avec suivi du chantier, coordination,
                            contrôle de l'avancement et comptes rendus réguliers.
                          </p>
                        </div>
                        <div data-testid="process-support-expertise-note" className="rounded-2xl border border-wood/40 bg-cream p-5">
                          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cognac">Essentiel ou Pack complet ?</p>
                          <p className="mt-2 text-sm leading-relaxed text-stone-500">
                            Vous n'avez pas besoin de le savoir dès aujourd'hui. Nous
                            définissons avec vous le niveau d'accompagnement adapté à votre
                            projet.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="mt-4 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">{s.text}</p>
                        {s.text2 && (
                          <p className="mt-3 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">{s.text2}</p>
                        )}
                        {i === 0 && (
                          <button
                            data-testid="process-step1-cta"
                            onClick={() => navigate("/estimer")}
                            className="group mt-6 flex w-fit items-center gap-2 text-sm font-bold text-cognac transition-colors duration-300 hover:text-navy"
                          >
                            Parler de mon projet
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div data-testid="process-after" className="mt-24 rounded-3xl bg-cream p-8 text-center sm:p-14">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac">Et après ?</p>
              <p className="mt-4 font-display text-2xl font-medium leading-snug text-navy sm:text-3xl">
                Profitez simplement de <span className="italic text-sage">votre nouvel espace.</span>
              </p>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-stone-500 sm:text-base">
                Parce qu'au fond, CozyHome ne parle pas seulement de travaux. Il
                s'agit de créer un lieu dans lequel vous aurez vraiment envie de vivre.
              </p>
              <button
                data-testid="process-page-cta"
                onClick={() => navigate("/estimer")}
                className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-navy px-9 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
              >
                Parler de mon projet
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
